import type { z } from "zod/v4";
import type { BrokerLeadCampaignFocus } from "./lead-gen-focus";
import type { LEAD_GEN_AD_LANGUAGES } from "./lead-gen-ad-languages";
import {
  CampaignBlueprintV1Schema,
  LEAD_GEN_BLUEPRINT_SCHEMA_NAME,
  LEAD_GEN_BLUEPRINT_SCHEMA_VERSION,
  type LeadGenCampaignConfig,
  type LeadGenCampaignPerformance,
  type CampaignBlueprintV1,
  type LeadGenBlueprintAngleSchema,
  type LeadGenTargetingShape,
} from "./lead-gen-blueprint";
function flatten(
  value: unknown,
  prefix = "",
  out: Record<string, string> = {},
) {
  if (Array.isArray(value)) {
    out[prefix] = JSON.stringify(value);
    return out;
  }
  if (value && typeof value === "object") {
    for (const [key, child] of Object.entries(
      value as Record<string, unknown>,
    )) {
      flatten(child, prefix ? `${prefix}.${key}` : key, out);
    }
    return out;
  }
  out[prefix] = JSON.stringify(value ?? null);
  return out;
}

export type LeadGenConfigDiff = {
  changed: string[];
  added: string[];
  removed: string[];
};

/** Path-level diff of two campaign configs. Arrays compare as a whole. */
export function diffLeadGenCampaignConfigs(
  previous: LeadGenCampaignConfig | null | undefined,
  next: LeadGenCampaignConfig,
): LeadGenConfigDiff {
  const before = flatten(previous ?? {});
  const after = flatten(next);
  const changed: string[] = [];
  const added: string[] = [];
  const removed: string[] = [];
  for (const key of Object.keys(after)) {
    if (!(key in before)) added.push(key);
    else if (before[key] !== after[key]) changed.push(key);
  }
  for (const key of Object.keys(before)) {
    if (!(key in after)) removed.push(key);
  }
  return {
    changed: changed.sort(),
    added: added.sort(),
    removed: removed.sort(),
  };
}

/** Semantic-layer blueprint from a campaign config; never includes Meta IDs. */
export function campaignConfigToBlueprint(
  config: LeadGenCampaignConfig,
  meta: {
    name: string;
    description?: string | null;
    tags?: string[];
    performance?: Partial<LeadGenCampaignPerformance> | null;
    sourceCampaignId?: string | null;
    sourceSnapshotVersion?: number | null;
    exportedAt?: Date;
    whyThisWorks?: string[];
    knownPitfalls?: string[];
  },
): CampaignBlueprintV1 {
  const winningIds = new Set(
    (meta.performance?.creatives ?? [])
      .filter((creative) => creative.leads > 0)
      .map((creative) => creative.id),
  );
  const angles = config.creatives
    .filter(
      (creative) =>
        creative.status !== "FAILED" &&
        creative.status !== "REPLACED" &&
        creative.status !== "RETIRED",
    )
    .map((creative) => ({
      key: undefined,
      name: creative.angle,
      language: creative.language,
      headline: creative.headline,
      primaryText: creative.primaryText,
      description: creative.description,
      bullets: creative.bullets,
      imageSceneName: creative.imageSceneName,
      note:
        creative.id && winningIds.has(creative.id)
          ? "Produced leads in the source campaign."
          : null,
      exemplar: true as const,
    }));

  return CampaignBlueprintV1Schema.parse({
    offer: config.offer ?? "BROKER_CONSULTATION",
    schema_name: LEAD_GEN_BLUEPRINT_SCHEMA_NAME,
    schema_version: LEAD_GEN_BLUEPRINT_SCHEMA_VERSION,
    name: meta.name,
    description: meta.description ?? null,
    tags: meta.tags ?? [],
    focus: config.focus,
    languages: config.adLanguages,
    geography: {
      depth: "county",
      recommendedMinAreas: Math.max(3, Math.min(config.territories.length, 8)),
      countries: Array.from(
        new Set(config.territories.map((territory) => territory.country)),
      ),
      note: `Source campaign ran ${config.territories.length} area${
        config.territories.length === 1 ? "" : "s"
      }. Re-select areas for the new broker.`,
    },
    targeting_shape: config.targeting,
    angles,
    image_direction: config.creatives
      .map((creative) => creative.imageSceneName)
      .filter((scene): scene is string => Boolean(scene)),
    form_rules: [],
    defaults: {
      monthlyAdBudgetCents: config.monthlyAdBudgetCents || null,
      creativesPerLanguage: 3,
    },
    why_this_works: meta.whyThisWorks ?? [],
    known_pitfalls: meta.knownPitfalls ?? [],
    pre_launch_gates: [],
    performance_summary: meta.performance ?? null,
    provenance: {
      exportedAt: (meta.exportedAt ?? new Date()).toISOString(),
      source: "venturu",
      sourceCampaignId: meta.sourceCampaignId ?? null,
      sourceSnapshotVersion: meta.sourceSnapshotVersion ?? null,
    },
  });
}

export type LeadGenBlueprintPrefill = {
  offer?: "BROKER_CONSULTATION" | "FREE_VALUATION";
  focus: BrokerLeadCampaignFocus;
  adLanguages: Array<(typeof LEAD_GEN_AD_LANGUAGES)[number]>;
  monthlyAdBudgetCents: number | null;
  targetingShape: LeadGenTargetingShape | null;
  recommendedMinAreas: number;
  exemplars: Array<z.infer<typeof LeadGenBlueprintAngleSchema>>;
  imageDirection: string[];
};

/** What a new campaign inherits from a playbook. Identity and areas are always re-collected. */
export function blueprintToDraftPrefill(
  blueprint: CampaignBlueprintV1,
): LeadGenBlueprintPrefill {
  return {
    offer: blueprint.offer ?? "BROKER_CONSULTATION",
    focus: blueprint.focus,
    adLanguages: blueprint.languages,
    monthlyAdBudgetCents: blueprint.defaults.monthlyAdBudgetCents,
    targetingShape: blueprint.targeting_shape,
    recommendedMinAreas: blueprint.geography.recommendedMinAreas,
    exemplars: blueprint.angles,
    imageDirection: blueprint.image_direction,
  };
}
